import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Search, 
  BookOpen, 
  Users, 
  Settings, 
  MessageSquare, 
  Bot, 
  Zap, 
  Puzzle,
  ChevronRight,
  ChevronDown,
  ArrowLeft,
  Rocket,
  Shield,
  BarChart3,
  Headphones,
  ExternalLink,
  CheckCircle,
  Info,
  Wrench
} from "lucide-react";
import { Input } from "@/components/ui/input";
import logoImage from "@/assets/logo-zavvi.png";

interface Category {
  id: string;
  title: string;
  icon: React.ReactNode;
  description: string;
  articles: Article[];
}

interface Article {
  id: string;
  title: string;
  content: string;
  keywords: string[];
}

const helpCategories: Category[] = [
  {
    id: "conheca-zavvi",
    title: "Conheça a Zavvi",
    icon: <BookOpen className="w-4 h-4" />,
    description: "Documentação para pessoas interessadas em assinar a Zavvi.ai.",
    articles: [
      { 
        id: "1", 
        title: "O que é a Zavvi.ai e para quem ela é ideal?", 
        content: `A Zavvi.ai é uma plataforma de gestão omnichannel de clientes projetada para centralizar a comunicação de múltiplos canais, com foco principal no WhatsApp. A solução oferece atendimento multicanal, automação e chatbots integrados.

A plataforma foi criada com o objetivo de oferecer soluções de autoatendimento 24h através de chatbots, gerenciamento de atendimentos com atendentes humanos e disparos de mensagens em massa via WhatsApp para o seu público-alvo.

Você pode integrar sua conta com vários contatos ao mesmo tempo, através de um painel que integra o seu WhatsApp de uma forma muito mais eficiente. É possível visualizar todos os seus atendimentos em um único lugar! Isso ajuda a reduzir o tempo de resposta e melhorar a qualidade das tratativas. Você também consegue gerenciar suas conversas e colaborar com sua equipe, a qualquer hora e de qualquer lugar.

**A Zavvi.ai é a solução perfeita para você se:**

• **Você é uma Empresa de E-commerce:** E quer centralizar o atendimento de vendas e suporte em um único lugar.

• **Você é uma Clínica ou Consultório:** E precisa agendar consultas e se comunicar com pacientes de forma organizada.

• **Você é uma Agência ou Empresa de Serviços:** E quer oferecer uma comunicação profissional para seus clientes.

• **Você quer escalar seu atendimento:** E entende que ter controle total também significa ter métricas e relatórios.`, 
        keywords: ["zavvi", "plataforma", "atendimento", "omnichannel", "multicanal"] 
      },
      { 
        id: "2", 
        title: "Recursos e Integrações", 
        content: `A Zavvi.ai centraliza todos os seus canais de comunicação (WhatsApp Oficial e não oficial, Instagram, Facebook, Telegram, etc.) e permite automações poderosas com chatbots e agentes de I.A.

**Principais funcionalidades:**

• **Gestão de múltiplos atendentes** - Organize sua equipe em filas e setores
• **Chatbots nativos** - Crie fluxos de automação sem código
• **Integração com IA** - ChatGPT e Gemini integrados
• **API Oficial do WhatsApp (WABA)** - Conexão estável e oficial
• **Conexão via QR Code** - Para testes e uso pessoal
• **Instagram e Facebook** - Integração com Meta
• **Telegram** - Suporte completo
• **Funil de Vendas e Kanban** - Gestão de oportunidades
• **Relatórios completos** - Métricas de atendimento
• **Disparo de campanhas** - Comunicação proativa em massa`, 
        keywords: ["funcionalidades", "recursos", "features", "integração"] 
      },
      { 
        id: "3", 
        title: "Planos e Preços", 
        content: `A Zavvi.ai oferece dois planos principais:

**Plano Light - R$397/mês (cobrado anualmente)**
• Ideal para pequenas empresas
• Inclui todos os recursos essenciais
• Conexões e agentes ilimitados

**Plano Elite - R$497/mês (cobrado anualmente)**
• Ideal para empresas em crescimento
• Recursos avançados e suporte prioritário
• Conexões e agentes ilimitados

---

**Serviços de Implementação (Cobranças Adicionais e Opcionais)**

A equipe Zavvi oferece serviços de implementação para garantir que você extraia o máximo da plataforma desde o início.

**Implementação Light - R$997** (de R$1.497 - 33% de desconto)
• Ideal para iniciantes
• Serviço opcional para o Plano Light
• Configuração básica inicial da conta e canais
• Treinamento completo do supervisor ou administrador da conta
• Suporte na primeira semana de uso

**Implementação Elite - R$1.497** (de R$3.497 - 57% de desconto)
• Ideal para empresas com estrutura no Facebook Business Manager
• Serviço opcional para o Plano Elite
• Treinamento completo do supervisor ou administrador da conta
• Suporte dedicado nas primeiras duas semanas
• Guia completo para a conexão através da API Oficial do WhatsApp

---

**Diferenciais Zavvi:**
• Suporte brasileiro especializado
• Sem cobrança por usuário/assento
• Conexões e agentes ilimitados`,
        keywords: ["planos", "preços", "valores", "assinatura", "light", "elite", "implementação"]
      },
    ]
  },
  {
    id: "central-assinante",
    title: "Central do Assinante",
    icon: <Users className="w-4 h-4" />,
    description: "Documentação sobre requisitos técnicos, políticas de uso, segurança e suporte.",
    articles: [
      { 
        id: "4", 
        title: "Atualizações e Status da Plataforma", 
        content: `Acompanhe sempre as últimas atualizações da plataforma Zavvi.ai.

**Organização da documentação:**

• **Changelog:** Aqui você encontra o registro completo de todas as versões já lançadas, com as mais recentes no topo. Sempre consulte as notas da versão para a qual você irá atualizar.

• **Status do Serviço:** Monitore a disponibilidade da plataforma em tempo real.

**Para acompanhar nossos avisos em tempo real:**
Entre em nosso canal de avisos do WhatsApp ou acompanhe as notificações dentro da plataforma.`, 
        keywords: ["atualizações", "status", "versão", "changelog"] 
      },
      { 
        id: "5", 
        title: "Pré-requisitos e Recursos Técnicos", 
        content: `Para utilizar a Zavvi.ai de forma otimizada, certifique-se de que seu ambiente atende aos seguintes requisitos:

**Requisitos de uso:**
• Navegador atualizado (Chrome, Firefox, Edge ou Safari)
• Conexão estável de internet
• Número de WhatsApp Business (para API Oficial)
• Conta no Facebook Business Manager (para Instagram/Facebook)

**Recursos técnicos disponíveis:**
• API para integrações personalizadas
• Webhooks para automações externas
• Integração com N8N e Make
• Suporte a Typebot integrado`, 
        keywords: ["técnicos", "requisitos", "configuração", "navegador"] 
      },
      { 
        id: "6", 
        title: "Políticas de Uso e Termos", 
        content: `Ao utilizar a Zavvi.ai, você concorda com nossos termos de uso e políticas:

**Diretrizes importantes:**

• **Uso responsável:** Não utilize a plataforma para spam ou mensagens não solicitadas
• **Conformidade LGPD:** Garanta que você tem consentimento dos contatos
• **API Oficial vs Não Oficial:** Entenda as diferenças e riscos de cada método de conexão

**API Oficial (WABA):**
É o método formal e suportado pela Meta. É a solução mais robusta e segura para empresas que buscam estabilidade e escala.
- Vantagens: Alta estabilidade, conformidade com os termos de serviço, menor risco de bloqueio.
- Considerações: Requer verificação da empresa no Facebook Business Manager.

**Conexão via QR Code:**
Este método automatiza uma sessão do WhatsApp, similar ao WhatsApp Web.
- Vantagens: Configuração rápida, sem custos da Meta por conversa.
- Considerações: Pode apresentar instabilidade e risco de bloqueio.`, 
        keywords: ["políticas", "termos", "uso", "lgpd", "api"] 
      },
      { 
        id: "gestao-assinatura", 
        title: "Gestão da Assinatura", 
        content: `Gerencie sua assinatura Zavvi.ai de forma simples:

**Informações da assinatura:**
• Acesse o painel Admin para visualizar seu plano atual
• Veja a data de renovação e histórico de pagamentos
• Gerencie métodos de pagamento

**Upgrade de plano:**
• Migre do Light para Elite a qualquer momento
• A diferença é calculada proporcionalmente
• Ative recursos adicionais imediatamente

**Cancelamento:**
• Seus dados ficam disponíveis por 30 dias após o cancelamento
• Exporte seus contatos antes de cancelar`,
        keywords: ["assinatura", "pagamento", "upgrade", "cancelamento"] 
      },
    ]
  },
  {
    id: "primeiro-acesso",
    title: "Primeiro Acesso",
    icon: <Rocket className="w-4 h-4" />,
    description: "Guias para novos clientes, desde a assinatura até a conexão do primeiro canal.",
    articles: [
      { 
        id: "7", 
        title: "Primeiros Passos - Novos Assinantes", 
        content: `Este guia detalha a sequência de ações recomendadas após a confirmação da sua assinatura Zavvi.ai.

**Primeiros Passos Após a Assinatura:**

**Passo 1: Verifique seu E-mail**
Após a compra, você receberá e-mails automáticos com:
• Link de acesso à plataforma
• Credenciais de login
• Instruções para o primeiro acesso

**Passo 2: Acesse a Plataforma**
Faça login no painel Zavvi com suas credenciais:
• Explore o painel administrativo
• Conheça as funcionalidades
• Acesse a documentação de ajuda

**Passo 3: Configure sua Equipe**
Após o primeiro acesso:
• Cadastre seus atendentes
• Defina filas e setores
• Configure horários de atendimento

**Passo 4: Convide sua Equipe**
Envie os convites para seus atendentes acessarem a plataforma.

**Passo 5: Comece a Atender!**
Sua operação está pronta para receber os primeiros clientes.`, 
        keywords: ["início", "começar", "novo", "passos"] 
      },
      { 
        id: "8", 
        title: "Primeiro Acesso ao Sistema", 
        content: `**1. Realizando o Primeiro Login**

Após a compra, você receberá todos os dados de acesso por e-mail.

**Dados que você receberá:**
• URL de acesso à plataforma
• Login e Senha do Administrador
• Login e Senha de usuário teste

**Passos para o Primeiro Acesso:**
1. Acesse a URL informada no e-mail
2. Utilize as credenciais de Administrador para fazer o login
3. Troque sua senha no primeiro acesso

**2. Entendendo a Estrutura: Admin vs Atendente**

A plataforma Zavvi.ai possui níveis de acesso distintos:

**Painel Admin (Administrador):**
É o painel de mais alto nível, onde você gerencia toda a operação:
• Gestão de usuários e equipes
• Configuração de canais
• Criação de chatbots
• Análise de relatórios
• Configurações gerais

**Painel Atendente:**
Usado pela equipe no dia a dia:
• Caixa de entrada de conversas
• Envio de mensagens
• Acesso a contatos
• Uso de respostas rápidas`, 
        keywords: ["login", "acesso", "primeiro", "perfil", "admin", "atendente"] 
      },
      { 
        id: "9", 
        title: "Conectando seu Primeiro Canal", 
        content: `**Passo 1: Acesse o Menu Canais**
No menu lateral do Painel Admin, acesse "Canais".

**Passo 2: Adicione um Novo Canal**
1. Clique no botão "Adicionar Canal"
2. Escolha o tipo de canal:
   - WhatsApp QR Code (conexão rápida)
   - WhatsApp API Oficial (WABA)
   - Instagram
   - Facebook Messenger
   - Telegram
3. Dê um nome para identificar a conexão
4. Configure as opções do canal
5. Clique em "Salvar"

**Para WhatsApp via QR Code:**
1. Após salvar, clique em "Novo QR Code"
2. Pegue o celular com o número de WhatsApp
3. No aplicativo, vá em Configurações > Aparelhos Conectados
4. Aponte a câmera para o QR Code
5. Aguarde a confirmação de conexão

**Passo 3: Teste a Conexão**
1. Acesse o menu "Atendimentos"
2. Envie uma mensagem de teste para o número conectado
3. A mensagem deve aparecer na caixa de entrada

Pronto! Você já pode receber e enviar mensagens.`, 
        keywords: ["conectar", "canal", "whatsapp", "instagram", "qr code"] 
      },
    ]
  },
  {
    id: "configuracao-admin",
    title: "Configuração Admin",
    icon: <Settings className="w-4 h-4" />,
    description: "Documentação do Painel Admin, incluindo gestão da conta e configurações gerais.",
    articles: [
      { 
        id: "10", 
        title: "Visão Geral do Painel Admin", 
        content: `As documentações agrupadas aqui representam os primeiros e mais importantes passos para preparar sua conta para o atendimento.

**1. Canais de Comunicação**
O primeiro passo para qualquer operação de atendimento é conectar os canais por onde seus clientes entrarão em contato.
• Visão Geral dos Canais
• Conectando seus Canais (Passo a Passo)

**2. Gestão de Atendentes**
Com os canais conectados, organize a equipe que irá atender:
• Gestão de Usuários
• Organização de Equipes
• Configuração de Filas de Atendimento

**3. Horário de Atendimento**
Defina o horário de funcionamento da sua empresa. Esta configuração é fundamental para o roteamento de conversas e mensagens automáticas de fora do expediente.

**4. Configurações Gerais**
Ajustes que afetam o comportamento do sistema:
• Configurações Gerais
• Configuração de Bots e IA
• Integrações
• CRM`, 
        keywords: ["admin", "painel", "administrativo", "visão geral"] 
      },
      { 
        id: "11", 
        title: "Configurações Gerais da Plataforma", 
        content: `Acesse as configurações gerais para personalizar o comportamento da plataforma:

**Configurações disponíveis:**

• **Dados da Empresa:** Nome, logo, cores da marca
• **Fuso Horário:** Defina o fuso horário da operação
• **Idioma:** Português (Brasil) padrão
• **Notificações:** Configure alertas e avisos

**Integrações:**
• ChatGPT/OpenAI
• Google Calendar
• Webhooks personalizados
• N8N/Make

**CRM Interno:**
• Campos personalizados de contatos
• Etiquetas e categorização
• Funil de vendas`, 
        keywords: ["configurações", "geral", "personalizar", "empresa"] 
      },
      { 
        id: "12", 
        title: "Gestão de Usuários", 
        content: `**Como cadastrar usuários:**

1. Acesse o menu "Usuários" no painel Admin
2. Clique em "Adicionar"
3. Preencha os dados:
   - Nome completo
   - E-mail de acesso
   - Senha temporária
   - Perfil de acesso

**Perfis disponíveis:**

• **Administrador:** Acesso total a todas as configurações
• **Supervisor:** Pode gerenciar equipes e ver relatórios
• **Atendente:** Acesso apenas à caixa de entrada

**Gerenciando usuários:**
• Edite permissões a qualquer momento
• Desative usuários sem excluir histórico
• Defina horários de atendimento por usuário
• Associe usuários a filas específicas`, 
        keywords: ["usuários", "cadastro", "permissões", "atendentes"] 
      },
      { 
        id: "13", 
        title: "Organização de Equipes e Filas", 
        content: `**Equipes:**
A funcionalidade de Equipes permite agrupar usuários para fins de organização e distribuição de conversas.

Como criar uma equipe:
1. Acesse "Equipes" no menu
2. Clique em "Adicionar"
3. Defina nome e descrição
4. Associe os membros

**Filas de Atendimento:**
As Filas direcionam as conversas recebidas para usuários ou equipes específicas (ex: Vendas, Suporte).

Como configurar filas:
1. Acesse "Filas" no menu
2. Clique em "Adicionar"
3. Configure:
   - Nome da fila
   - Equipe/Usuários responsáveis
   - Mensagem de boas-vindas
   - Horário de funcionamento

**Roteamento automático:**
• Distribua conversas entre atendentes
• Configure prioridades
• Defina limites de atendimentos simultâneos`, 
        keywords: ["equipes", "filas", "organização", "setores"] 
      },
    ]
  },
  {
    id: "canais-comunicacao",
    title: "Canais de Comunicação",
    icon: <MessageSquare className="w-4 h-4" />,
    description: "Guias para conexão e configuração de todos os canais de atendimento.",
    articles: [
      { 
        id: "14", 
        title: "Visão Geral dos Canais", 
        content: `A capacidade de centralizar múltiplos canais de comunicação em uma única plataforma é uma das principais funcionalidades da Zavvi.ai.

**Canais WhatsApp: A Escolha Mais Importante**

Para o WhatsApp, oferecemos duas categorias de conexão:

**1. API Oficial (WABA - WhatsApp Business API)**
É o método formal e suportado pela Meta.
• Vantagens: Alta estabilidade, conformidade com termos, menor risco de bloqueio, acesso a recursos corporativos.
• Considerações: Requer verificação da empresa no Facebook Business Manager e possui custos baseados no volume de conversas.

**2. Conexão via QR Code (não oficial)**
Automatiza uma sessão do WhatsApp, similar ao WhatsApp Web.
• Vantagens: Configuração rápida, sem custos por conversa.
• Considerações: Pode apresentar instabilidade e risco de bloqueio.

**Outros Canais:**
• Instagram Direct
• Facebook Messenger
• Telegram
• Webchat (para seu site)
• E-mail`, 
        keywords: ["canais", "whatsapp", "instagram", "visão geral"] 
      },
      { 
        id: "15", 
        title: "WhatsApp API Oficial (WABA)", 
        content: `A API Oficial do WhatsApp Business (WABA) é a conexão mais estável e recomendada para empresas.

**Pré-requisitos:**
• Conta verificada no Facebook Business Manager
• Número de telefone dedicado (não pode estar em uso no WhatsApp)
• CNPJ da empresa

**Processo de conexão:**
1. Acesse "Canais" > "Adicionar Canal"
2. Selecione "WhatsApp API Oficial"
3. Conecte sua conta do Facebook Business
4. Verifique sua empresa (se ainda não verificada)
5. Registre o número de telefone
6. Aguarde aprovação (geralmente 24-48h)

**Particularidades do WABA:**
• Janela de 24 horas para resposta
• Mensagens de Template para iniciar conversas
• Custo por conversa cobrado pela Meta`, 
        keywords: ["whatsapp", "api", "oficial", "waba", "meta"] 
      },
      { 
        id: "16", 
        title: "WhatsApp via QR Code", 
        content: `A conexão via QR Code é ideal para testes iniciais ou operações menores.

**Como conectar:**
1. Acesse "Canais" > "Adicionar Canal"
2. Selecione "WhatsApp QR Code"
3. Dê um nome para o canal
4. Clique em "Salvar"
5. Clique em "Novo QR Code"
6. No celular, abra o WhatsApp
7. Vá em Configurações > Aparelhos Conectados
8. Escaneie o QR Code exibido

**Importante:**
• Mantenha o celular conectado à internet
• A sessão pode desconectar periodicamente
• Reconecte escaneando novo QR Code quando necessário

**Riscos:**
• Não é método oficial da Meta
• Pode haver bloqueio do número
• Menos estável que a API Oficial`, 
        keywords: ["whatsapp", "qr code", "conectar", "baileys"] 
      },
      { 
        id: "17", 
        title: "Instagram e Facebook", 
        content: `Conecte suas redes sociais para centralizar o atendimento.

**Instagram Direct:**
1. Tenha uma conta comercial no Instagram
2. Conecte ao Facebook Business Manager
3. Na Zavvi, acesse "Canais" > "Adicionar Canal"
4. Selecione "Instagram"
5. Faça login com sua conta do Facebook
6. Autorize a conexão

**Facebook Messenger:**
1. Tenha uma página no Facebook
2. Na Zavvi, acesse "Canais" > "Adicionar Canal"
3. Selecione "Facebook Messenger"
4. Faça login e selecione a página
5. Autorize a conexão

**Telegram:**
1. Crie um bot no Telegram via @BotFather
2. Copie o token do bot
3. Na Zavvi, adicione canal "Telegram"
4. Cole o token e salve`, 
        keywords: ["instagram", "facebook", "messenger", "telegram"] 
      },
    ]
  },
  {
    id: "ferramentas-atendimento",
    title: "Ferramentas do Atendimento",
    icon: <Headphones className="w-4 h-4" />,
    description: "Ferramentas que sua equipe utilizará no dia a dia para interagir com os clientes.",
    articles: [
      { 
        id: "18", 
        title: "Tela de Atendimento", 
        content: `Esta é a tela principal onde sua equipe irá interagir com os clientes no dia a dia. É a sua caixa de entrada unificada, projetada para gerenciar diferentes tipos de conversas em um único lugar.

**Como acessar:**
Clique no ícone de "Atendimentos" no menu lateral.

**Funcionalidades da tela:**
• Lista de conversas ativas
• Filtros por status, fila e atendente
• Histórico completo do contato
• Barra de ferramentas de atendimento

**Status das conversas:**
• Aguardando: Conversa na fila esperando atendente
• Em atendimento: Atendente assumiu a conversa
• Pendente: Aguardando resposta do cliente
• Resolvido: Conversa finalizada

**Ações disponíveis:**
• Aceitar/Iniciar atendimento
• Transferir para outro atendente/fila
• Adicionar etiquetas e notas
• Encerrar conversa`, 
        keywords: ["atendimento", "tela", "caixa entrada", "conversas"] 
      },
      { 
        id: "19", 
        title: "Gestão de Contatos", 
        content: `O painel de Contatos centraliza todas as informações dos seus clientes.

**Como acessar:**
Clique no ícone de "Contatos" no menu lateral.

**Funcionalidades:**
• Busca por nome ou número
• Filtro por carteira ou etiqueta
• Sincronização automática de contatos
• Importação/Exportação em Excel

**Criar contato manualmente:**
1. Clique em "Adicionar"
2. Preencha os campos:
   - Nome
   - Número (com DDI e DDD)
   - E-mail
   - CPF/CNPJ
   - Data de nascimento
   - Empresa
   - Carteira (atendente responsável)
   - Etiqueta
3. Clique em "Salvar"

**Gerenciando contatos:**
• Inicie novo atendimento direto do contato
• Edite informações a qualquer momento
• Visualize histórico de conversas
• Exclua contatos quando necessário`, 
        keywords: ["contatos", "clientes", "cadastro", "gestão"] 
      },
      { 
        id: "20", 
        title: "Mensagens Rápidas", 
        content: `As mensagens rápidas agilizam o atendimento através de textos predefinidos.

**Exemplos de uso:**
• Boas-vindas
• Tabela de preços
• Informações de horário
• Dados bancários para pagamento

**Como criar uma mensagem rápida:**
1. Acesse "Mensagens Rápidas" no menu
2. Clique em "Adicionar"
3. Preencha:
   - Chave: palavra para identificar (ex: /precos)
   - Mensagem: texto completo
   - Arquivo: anexo opcional
   - Áudio: gravação opcional
4. Clique em "Salvar"

**Como usar no atendimento:**
1. Durante uma conversa, digite "/"
2. Selecione a mensagem desejada
3. Ou digite a chave completa (ex: /precos)
4. A mensagem será inserida automaticamente`, 
        keywords: ["mensagens", "rápidas", "atalhos", "predefinidas"] 
      },
      { 
        id: "21", 
        title: "Etiquetas e Notas", 
        content: `**Etiquetas:**
Ferramenta para classificar atendimentos e contatos visualmente.

Como criar uma etiqueta:
1. Acesse "Etiquetas" no menu
2. Clique em "Adicionar"
3. Defina:
   - Nome (ex: Lead Quente, Cliente VIP)
   - Cor
4. Salve

Aplicando etiquetas:
• Durante o atendimento, clique no ícone de etiqueta
• Selecione uma ou mais etiquetas
• As etiquetas aparecem visualmente na conversa

**Notas:**
Registre informações internas que apenas a equipe pode ver.

Como adicionar notas:
1. Durante o atendimento, clique no ícone de nota
2. Digite a informação
3. A nota fica visível apenas para atendentes
4. Útil para: preferências do cliente, histórico, observações`, 
        keywords: ["etiquetas", "tags", "notas", "organização"] 
      },
      { 
        id: "fechamentos", 
        title: "Fechamentos e Avaliações", 
        content: `**Mensagens de Fechamento:**
Configure mensagens enviadas ao finalizar um atendimento.

Como configurar:
1. Acesse "Fechamentos" no menu
2. Clique em "Adicionar"
3. Defina:
   - Nome do fechamento (ex: Venda Realizada, Sem Interesse)
   - Mensagem de despedida (opcional)
   - Se envia pesquisa de satisfação
4. Salve

**Avaliações:**
Colete feedback dos clientes após o atendimento.

Tipos de avaliação:
• NPS (0 a 10)
• Estrelas (1 a 5)
• Positivo/Negativo

Como funciona:
1. Ao encerrar, selecione o tipo de fechamento
2. Se configurado, cliente recebe pesquisa
3. Respostas ficam registradas no atendimento
4. Visualize métricas em Relatórios`, 
        keywords: ["fechamento", "encerrar", "avaliação", "nps"] 
      },
    ]
  },
  {
    id: "automacao-chatbots",
    title: "Automação e Chatbots",
    icon: <Bot className="w-4 h-4" />,
    description: "Documentação sobre como criar e gerenciar automações e chatbots.",
    articles: [
      { 
        id: "22", 
        title: "Chatbot Nativo - Construtor de Fluxo", 
        content: `O Construtor de Fluxo é a ferramenta nativa da Zavvi.ai para criação de chatbots e automações de conversa.

**Conceitos Fundamentais:**

1. **Fluxo:** O conjunto completo de todas as etapas e regras da automação.
2. **Etapa (ou Nó):** Cada "caixa" dentro do fluxo, representando um momento da conversa.
3. **Interações (Ações):** Ações que o chatbot executa (enviar mensagem, adicionar tag).
4. **Condições (Regras):** Regras que determinam para qual etapa seguir.

**Interações disponíveis:**

• **Comunicação:** Enviar mensagem, mídia, SMS, agendar mensagem
• **Controle do Fluxo:** Delay, adicionar outro fluxo, bloquear chatbot
• **Gestão de Dados:** Adicionar tag, adicionar ao Kanban, criar oportunidade
• **Integrações:** Webhook, Typebot, ChatGPT, Google Agenda

**Condições:**
• Qualquer resposta
• Resposta igual a / contém / começa com
• Validação por Regex (CPF, e-mail, etc.)`, 
        keywords: ["chatbot", "fluxo", "automação", "construtor"] 
      },
      { 
        id: "23", 
        title: "Configurações do Chatbot", 
        content: `**Configurações Gerais do Fluxo:**

No painel de configurações de cada fluxo, você pode ajustar:

• **Mensagem de Transferência:** Enviada quando o bot transfere para atendente.
• **Mensagem de Fallback:** Resposta quando não entende o cliente.
• **Palavra-Gatilho:** Palavra que reinicia o fluxo a qualquer momento.
• **Timeout:** Ação se o cliente não responder em tempo determinado.
• **Horário de Atendimento:** Comportamento fora do horário comercial.

**Ativando o Fluxo em um Canal:**

Um fluxo criado não faz nada até ser conectado a um canal:

1. Acesse o menu "Canais"
2. Localize o canal desejado
3. Clique no ícone de "Chatbot"
4. Selecione o fluxo na lista
5. Toda nova conversa será gerenciada pelo fluxo`, 
        keywords: ["configuração", "fluxo", "ativar", "canal"] 
      },
      { 
        id: "24", 
        title: "Variáveis e Personalização", 
        content: `O construtor permite capturar respostas do cliente e reutilizá-las para personalizar a conversa.

**Como Capturar:**
1. Em cada etapa, habilite "Capturar Variável"
2. Dê um nome (ex: nome_cliente)
3. A resposta do cliente será armazenada

**Como Usar:**
Use chaves duplas em mensagens posteriores:
• "Olá, {{nome_cliente}}! Como posso ajudar?"

**Variáveis de Sistema:**
• {{contact.name}} - Nome do contato
• {{agent.name}} - Nome do atendente
• {{ticket.protocol}} - Número do protocolo

**Importante:**
O sistema opera de forma sequencial. Você só pode usar o valor de uma variável capturada na etapa imediatamente anterior.`, 
        keywords: ["variáveis", "personalização", "captura", "dados"] 
      },
      { 
        id: "25", 
        title: "Integração com IA (ChatGPT)", 
        content: `Integre inteligência artificial no seu atendimento.

**Configuração:**
1. Acesse Configurações > Integrações
2. Selecione "OpenAI/ChatGPT"
3. Insira sua chave de API da OpenAI
4. Defina o modelo (GPT-3.5, GPT-4, etc.)

**Usando no Chatbot:**
1. No fluxo, adicione ação "Chamar ChatGPT"
2. Configure o prompt (instruções para a IA)
3. Defina contexto e limitações
4. A resposta da IA é enviada ao cliente

**Boas práticas:**
• Seja específico no prompt
• Defina limites claros de atuação
• Configure fallback para casos de erro
• Monitore as respostas inicialmente`, 
        keywords: ["ia", "chatgpt", "inteligência artificial", "openai"] 
      },
    ]
  },
  {
    id: "campanhas-disparos",
    title: "Campanhas e Disparos",
    icon: <Zap className="w-4 h-4" />,
    description: "Guias para envio de comunicação proativa e campanhas em massa.",
    articles: [
      { 
        id: "26", 
        title: "Criando Campanhas", 
        content: `Campanhas permitem enviar mensagens para múltiplos contatos de forma organizada.

**Como criar uma campanha:**
1. Acesse "Campanhas" no menu
2. Clique em "Nova Campanha"
3. Configure:
   - Nome da campanha
   - Canal de envio
   - Público-alvo (lista ou segmento)
   - Mensagem (texto, mídia ou template)
   - Agendamento (imediato ou programado)
4. Revise e inicie

**Tipos de campanha:**
• Promocional: Ofertas e descontos
• Informativa: Comunicados e novidades
• Reengajamento: Contatos inativos
• Follow-up: Sequência de mensagens

**Para API Oficial (WABA):**
Você deve usar Templates aprovados pela Meta para iniciar conversas.`, 
        keywords: ["campanhas", "criar", "envio", "marketing"] 
      },
      { 
        id: "27", 
        title: "Disparos em Massa", 
        content: `Envie mensagens para grandes volumes de contatos.

**Preparação:**
1. Prepare sua lista de contatos
2. Segmente por interesse/perfil
3. Crie a mensagem (respeite boas práticas)

**Boas práticas para disparos:**
• Envie apenas para quem optou por receber
• Personalize com variáveis (nome, etc.)
• Evite horários inadequados
• Respeite limites de envio por hora
• Monitore taxa de bloqueio

**Limites recomendados:**
• Inicie com volumes baixos (100-500/dia)
• Aumente gradualmente
• Pare se houver muitos bloqueios

**Métricas de disparo:**
• Enviados
• Entregues
• Lidos
• Respondidos
• Erros/Falhas`, 
        keywords: ["disparos", "massa", "bulk", "envio", "volume"] 
      },
      { 
        id: "28", 
        title: "Segmentação de Contatos", 
        content: `Organize contatos em grupos para campanhas direcionadas.

**Formas de segmentar:**

**Por Etiquetas:**
• Crie etiquetas descritivas
• Aplique durante atendimentos
• Filtre por etiqueta nas campanhas

**Por Campos Personalizados:**
• Cidade, Estado
• Interesse/Produto
• Fonte de aquisição
• Data de cadastro

**Por Comportamento:**
• Última interação
• Compras realizadas
• Atendimentos anteriores

**Criando listas:**
1. Acesse "Contatos"
2. Aplique os filtros desejados
3. Exporte ou use direto na campanha

**Dica:** Quanto mais segmentado, maior a taxa de resposta e menor o risco de bloqueio.`, 
        keywords: ["segmentação", "listas", "grupos", "público"] 
      },
    ]
  },
  {
    id: "integracoes",
    title: "Ferramentas e Integrações",
    icon: <Puzzle className="w-4 h-4" />,
    description: "Funcionalidades adicionais e integrações com sistemas externos.",
    articles: [
      { 
        id: "29", 
        title: "Visão Geral de Integrações", 
        content: `Esta seção agrupa ferramentas que expandem as capacidades da Zavvi.ai além do atendimento convencional.

**Integrações disponíveis:**

• **Typebot:** Construtor visual de chatbots avançados
• **N8N/Make:** Automações e workflows externos
• **OpenAI:** ChatGPT e IA generativa
• **Google Calendar:** Agendamentos automáticos
• **Webhooks:** Conexão com qualquer sistema

**CRM e Vendas:**
• Funil de Oportunidades
• Kanban de Atendimentos
• Campos personalizados

**Notificações:**
• Push notifications
• E-mail
• SMS (via integrações)

**API para Desenvolvedores:**
Conecte a Zavvi.ai a sistemas externos via nossa API RESTful.`, 
        keywords: ["integrações", "api", "conexões", "sistemas"] 
      },
      { 
        id: "30", 
        title: "Funil de Oportunidades", 
        content: `Gerencie suas vendas e oportunidades com o Funil integrado.

**Como funciona:**
• Crie etapas do seu processo de vendas
• Mova contatos entre etapas
• Acompanhe valor e probabilidade
• Visualize métricas em tempo real

**Configurando o Funil:**
1. Acesse "Funil" no menu
2. Configure as etapas:
   - Prospecção
   - Qualificação
   - Proposta
   - Negociação
   - Fechamento
3. Defina valores e probabilidades

**Ações automáticas:**
Configure ações ao mover entre etapas:
• Enviar mensagem automática
• Adicionar etiqueta
• Notificar responsável
• Criar tarefa de follow-up`, 
        keywords: ["funil", "vendas", "oportunidades", "crm"] 
      },
      { 
        id: "31", 
        title: "Kanban de Atendimentos", 
        content: `Visualize e organize atendimentos no formato Kanban.

**Benefícios:**
• Visão rápida do status de cada atendimento
• Arrastar e soltar para mover
• Identificação visual por cores
• Acompanhamento de prazos

**Colunas padrão:**
• Aguardando
• Em atendimento
• Aguardando cliente
• Resolvido

**Personalizando:**
1. Acesse "Kanban" no menu
2. Configure colunas e ordem
3. Defina regras de cores
4. Ative filtros por equipe/atendente

**Uso no dia a dia:**
• Supervisores acompanham toda a operação
• Atendentes focam em suas colunas
• Alertas de SLA em vermelho`, 
        keywords: ["kanban", "visual", "board", "organização"] 
      },
      { 
        id: "32", 
        title: "API para Desenvolvedores", 
        content: `A API da Zavvi.ai permite integração programática com sistemas externos.

**Recursos da API:**
• Enviar mensagens
• Buscar contatos
• Criar/atualizar tickets
• Webhooks de eventos

**Autenticação:**
• Token de API no painel Admin
• Bearer token nas requisições
• Rate limiting aplicado

**Exemplos de uso:**
• Integração com ERP/CRM
• Automações personalizadas
• Dashboard customizado
• Disparo via sistema próprio

**Webhooks:**
Receba eventos em tempo real:
• Nova mensagem recebida
• Ticket criado/fechado
• Contato atualizado
• Avaliação recebida

Para documentação técnica completa, entre em contato com o suporte.`, 
        keywords: ["api", "desenvolvedores", "webhook", "integração"] 
      },
    ]
  },
  {
    id: "relatorios",
    title: "Relatórios e Métricas",
    icon: <BarChart3 className="w-4 h-4" />,
    description: "Análises e relatórios de desempenho do atendimento.",
    articles: [
      { 
        id: "33", 
        title: "Visão Geral dos Relatórios", 
        content: `Os relatórios são essenciais para gerenciar sua equipe de atendimento.

**Como acessar:**
Clique no ícone de "Relatórios" no menu lateral.

**Tipos de relatórios:**

**Resumo Geral:**
• Total de atendimentos
• Tempo médio de resposta
• Tempo médio de atendimento
• Taxa de resolução

**Por Atendente:**
• Atendimentos por pessoa
• Tempo de resposta individual
• Avaliações recebidas
• Mensagens enviadas

**Por Canal:**
• Volume por WhatsApp, Instagram, etc.
• Horários de pico
• Origem das conversas

**Por Período:**
• Comparativo semanal/mensal
• Tendências de volume
• Sazonalidade`, 
        keywords: ["relatórios", "métricas", "dashboard", "análise"] 
      },
      { 
        id: "34", 
        title: "Métricas de Atendimento", 
        content: `Entenda as principais métricas da sua operação:

**Tempo de Primeira Resposta (TFR):**
Quanto tempo leva para o primeiro contato com o cliente.
• Meta recomendada: < 5 minutos

**Tempo Médio de Atendimento (TMA):**
Duração total do atendimento até a resolução.
• Varia por tipo de negócio

**Taxa de Resolução:**
Percentual de atendimentos resolvidos vs total.
• Meta recomendada: > 90%

**NPS (Net Promoter Score):**
Satisfação do cliente de 0 a 10.
• Promotores: 9-10
• Neutros: 7-8
• Detratores: 0-6

**Volume por Hora:**
Identifique horários de pico para dimensionar equipe.`, 
        keywords: ["métricas", "kpi", "indicadores", "performance"] 
      },
      { 
        id: "35", 
        title: "Exportação de Dados", 
        content: `Exporte dados para análises externas.

**O que pode exportar:**
• Lista de contatos (Excel/CSV)
• Histórico de atendimentos
• Relatório de mensagens
• Dados de avaliações

**Como exportar:**
1. Acesse o relatório desejado
2. Aplique filtros se necessário
3. Clique em "Exportar"
4. Escolha o formato (Excel ou CSV)
5. Aguarde o download

**Agendamento de relatórios:**
Configure envio automático por e-mail:
• Diário, semanal ou mensal
• Para múltiplos destinatários
• Em formato Excel ou PDF`, 
        keywords: ["exportar", "excel", "csv", "download", "dados"] 
      },
    ]
  },
  {
    id: "seguranca",
    title: "Segurança e Privacidade",
    icon: <Shield className="w-4 h-4" />,
    description: "Políticas de segurança, LGPD e proteção de dados.",
    articles: [
      { 
        id: "36", 
        title: "Política de Privacidade", 
        content: `A Zavvi.ai está comprometida com a proteção dos seus dados.

**Dados que coletamos:**
• Informações de cadastro
• Dados de contatos e conversas
• Logs de acesso e uso

**Como usamos:**
• Prestação do serviço
• Melhorias na plataforma
• Suporte ao cliente
• Comunicações importantes

**Compartilhamento:**
• Não vendemos dados
• Compartilhamos apenas quando necessário para o serviço
• Provedores de infraestrutura com acordos de sigilo

**Seus direitos:**
• Acesso aos seus dados
• Correção de informações
• Exclusão (quando possível)
• Portabilidade`, 
        keywords: ["privacidade", "dados", "política", "direitos"] 
      },
      { 
        id: "37", 
        title: "Segurança da Plataforma", 
        content: `Medidas de segurança implementadas na Zavvi.ai:

**Infraestrutura:**
• Servidores em data centers certificados
• Criptografia em trânsito (SSL/TLS)
• Backups automáticos diários
• Monitoramento 24/7

**Acesso:**
• Autenticação segura
• Controle de permissões por perfil
• Logs de todas as ações
• Sessões com timeout

**Dados:**
• Criptografia em repouso
• Isolamento entre contas
• Acesso restrito a dados sensíveis

**Boas práticas recomendadas:**
• Use senhas fortes
• Não compartilhe credenciais
• Ative todas as camadas de segurança
• Revise acessos periodicamente`, 
        keywords: ["segurança", "proteção", "ssl", "criptografia"] 
      },
      { 
        id: "38", 
        title: "LGPD Compliance", 
        content: `A Zavvi.ai está em conformidade com a Lei Geral de Proteção de Dados (LGPD).

**Nossas responsabilidades:**
• Proteção técnica dos dados
• Transparência no uso
• Atendimento a solicitações
• Notificação de incidentes

**Suas responsabilidades como usuário:**
• Obter consentimento dos contatos
• Informar finalidade do tratamento
• Atender solicitações dos titulares
• Manter registros de consentimento

**Ferramentas disponíveis:**
• Exportação de dados de contato
• Exclusão de dados quando solicitado
• Logs de consentimento
• Termos configuráveis no chatbot

**DPO (Encarregado):**
Para solicitações relacionadas à LGPD, entre em contato: privacidade@zavvi.ai`, 
        keywords: ["lgpd", "compliance", "lei", "proteção"] 
      },
    ]
  },
];

const HelpCenter = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [selectedArticle, setSelectedArticle] = useState<Article | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<Category | null>(null);

  const filteredCategories = useMemo(() => {
    if (!searchQuery.trim()) return helpCategories;
    
    const query = searchQuery.toLowerCase();
    return helpCategories.map(category => ({
      ...category,
      articles: category.articles.filter(article => 
        article.title.toLowerCase().includes(query) ||
        article.content.toLowerCase().includes(query) ||
        article.keywords.some(kw => kw.includes(query))
      )
    })).filter(category => 
      category.articles.length > 0 || 
      category.title.toLowerCase().includes(query)
    );
  }, [searchQuery]);

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleCategoryClick = (category: Category) => {
    setSelectedCategory(category);
    setSelectedArticle(null);
  };

  const handleArticleClick = (article: Article, category: Category) => {
    setSelectedArticle(article);
    setSelectedCategory(category);
  };

  const handleBackToHome = () => {
    setSelectedArticle(null);
    setSelectedCategory(null);
  };

  const handleBackToCategory = () => {
    setSelectedArticle(null);
  };

  // Format content with markdown-like styling
  const formatContent = (content: string) => {
    return content.split('\n').map((line, index) => {
      // Headers
      if (line.startsWith('**') && line.endsWith('**')) {
        return (
          <h3 key={index} className="font-bold text-foreground mt-6 mb-3 text-lg">
            {line.replace(/\*\*/g, '')}
          </h3>
        );
      }
      // Bold sections (headers within content)
      if (line.includes('**')) {
        const parts = line.split(/(\*\*.*?\*\*)/g);
        return (
          <p key={index} className="text-muted-foreground leading-relaxed mb-2">
            {parts.map((part, i) => {
              if (part.startsWith('**') && part.endsWith('**')) {
                return <strong key={i} className="text-foreground">{part.replace(/\*\*/g, '')}</strong>;
              }
              return part;
            })}
          </p>
        );
      }
      // Bullet points
      if (line.startsWith('•')) {
        return (
          <li key={index} className="text-muted-foreground ml-4 mb-1 list-disc list-inside">
            {line.substring(1).trim()}
          </li>
        );
      }
      // Numbered items
      if (/^\d+\./.test(line)) {
        return (
          <li key={index} className="text-muted-foreground ml-4 mb-1 list-decimal list-inside">
            {line.replace(/^\d+\.\s*/, '')}
          </li>
        );
      }
      // Empty lines
      if (line.trim() === '') {
        return <br key={index} />;
      }
      // Regular paragraphs
      return (
        <p key={index} className="text-muted-foreground leading-relaxed mb-2">
          {line}
        </p>
      );
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass-card py-3 md:py-4">
        <div className="container mx-auto px-4 flex items-center justify-between">
          <Link to="/ajuda" onClick={handleBackToHome} className="flex items-center gap-3">
            <img src={logoImage} alt="Zavvi.ai" className="h-7 md:h-9 object-contain" />
            <span className="text-foreground font-medium text-sm md:text-base">Central de Ajuda</span>
          </Link>
          
          <div className="flex items-center gap-4">
            <div className="relative hidden md:block w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                type="text"
                placeholder="Buscar... (Ctrl+K)"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 bg-muted/50 border-border text-sm"
              />
            </div>
            <Link 
              to="/" 
              className="text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
            >
              <ExternalLink size={14} />
              <span className="hidden md:inline">Voltar ao Site</span>
            </Link>
          </div>
        </div>
      </header>

      <div className="pt-20 md:pt-24">
        <div className="flex">
          {/* Sidebar */}
          <aside className="hidden lg:block fixed left-0 top-20 bottom-0 w-72 border-r border-border bg-card/30 overflow-y-auto">
            <div className="p-4">
              <Link 
                to="/ajuda" 
                onClick={handleBackToHome}
                className="flex items-center gap-2 text-primary font-medium mb-4 hover:text-primary/80 transition-colors"
              >
                <BookOpen size={18} />
                Central de Ajuda Zavvi
              </Link>
              
              <nav className="space-y-1">
                {filteredCategories.map((category) => (
                  <div key={category.id}>
                    <button
                      onClick={() => toggleCategory(category.id)}
                      className={`w-full flex items-center justify-between gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                        selectedCategory?.id === category.id 
                          ? 'bg-primary/10 text-primary' 
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        {category.icon}
                        <span>{category.title}</span>
                      </div>
                      {expandedCategories.includes(category.id) ? (
                        <ChevronDown size={14} />
                      ) : (
                        <ChevronRight size={14} />
                      )}
                    </button>
                    
                    {expandedCategories.includes(category.id) && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        className="ml-6 mt-1 space-y-1"
                      >
                        {category.articles.map((article) => (
                          <button
                            key={article.id}
                            onClick={() => handleArticleClick(article, category)}
                            className={`w-full text-left px-3 py-1.5 text-xs rounded transition-colors ${
                              selectedArticle?.id === article.id
                                ? 'text-primary bg-primary/5'
                                : 'text-muted-foreground hover:text-foreground'
                            }`}
                          >
                            {article.title}
                          </button>
                        ))}
                      </motion.div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 lg:ml-72 min-h-[calc(100vh-5rem)]">
            <div className="container mx-auto px-4 py-8 max-w-4xl">
              
              {/* Mobile Search */}
              <div className="lg:hidden mb-6">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Buscar artigos..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-9 bg-muted/50 border-border"
                  />
                </div>
              </div>

              {!selectedCategory && !selectedArticle ? (
                /* Home View */
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4 }}
                >
                  {/* Hero */}
                  <div className="text-center mb-12">
                    <div className="flex justify-center mb-6">
                      <img src={logoImage} alt="Zavvi.ai" className="h-12 md:h-16 object-contain" />
                    </div>
                    <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                      Central de Ajuda Zavvi.ai
                    </h1>
                    <p className="text-muted-foreground max-w-2xl mx-auto">
                      A Zavvi.ai é uma plataforma de gestão omnichannel de clientes projetada para centralizar a comunicação de múltiplos canais, com foco principal no WhatsApp. Aqui você encontra toda a documentação para usar a plataforma.
                    </p>
                  </div>

                  {/* How to find info */}
                  <div className="glass-card rounded-xl p-6 mb-8">
                    <h2 className="text-xl font-bold text-foreground mb-4">Como Encontrar as Informações</h2>
                    <p className="text-muted-foreground mb-4">Existem duas formas de localizar o conteúdo:</p>
                    
                    <div className="grid md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                          <Search size={16} className="text-primary" />
                          Barra de Busca
                        </h3>
                        <p className="text-sm text-muted-foreground mb-2">
                          Para uma resposta rápida, utilize a barra de busca. Pesquise por termos como:
                        </p>
                        <ul className="text-sm space-y-1">
                          <li><code className="bg-muted px-2 py-0.5 rounded">Conectar canal</code></li>
                          <li><code className="bg-muted px-2 py-0.5 rounded">Chatbot</code></li>
                          <li><code className="bg-muted px-2 py-0.5 rounded">WhatsApp</code></li>
                        </ul>
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground mb-2 flex items-center gap-2">
                          <BookOpen size={16} className="text-primary" />
                          Navegação por Categorias
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Para explorar sobre uma área específica, navegue pelas categorias temáticas na barra esquerda ou nos cards abaixo. Elas estão organizadas por função para guiar o usuário.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Categories Grid */}
                  <div className="grid md:grid-cols-2 gap-4">
                    {helpCategories.map((category, index) => (
                      <motion.button
                        key={category.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                        onClick={() => handleCategoryClick(category)}
                        className="text-left glass-card rounded-xl p-5 hover:border-primary/50 transition-all group"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center text-primary group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                            {category.icon}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-semibold text-foreground mb-1 group-hover:text-primary transition-colors">
                              {category.title}
                            </h3>
                            <p className="text-xs text-muted-foreground line-clamp-2">
                              {category.description}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors mt-1" />
                        </div>
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              ) : selectedArticle ? (
                /* Article View */
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <button
                    onClick={handleBackToCategory}
                    className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
                  >
                    <ArrowLeft size={16} />
                    Voltar para {selectedCategory?.title}
                  </button>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
                    <button onClick={handleBackToHome} className="hover:text-foreground transition-colors">
                      Central de Ajuda
                    </button>
                    <ChevronRight size={14} />
                    <button onClick={handleBackToCategory} className="hover:text-foreground transition-colors">
                      {selectedCategory?.title}
                    </button>
                    <ChevronRight size={14} />
                    <span className="text-foreground">{selectedArticle.title}</span>
                  </div>

                  <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-6">
                    {selectedArticle.title}
                  </h1>

                  <div className="glass-card rounded-xl p-6 md:p-8">
                    <div className="prose prose-invert max-w-none">
                      {formatContent(selectedArticle.content)}
                      
                      <div className="mt-8 pt-6 border-t border-border">
                        <div className="flex items-start gap-3 p-4 bg-primary/5 rounded-lg">
                          <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-sm text-foreground font-medium mb-1">
                              Precisa de mais ajuda?
                            </p>
                            <p className="text-sm text-muted-foreground">
                              Entre em contato com nosso suporte via WhatsApp ou acesse outras documentações relacionadas.
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Related Articles */}
                  {selectedCategory && selectedCategory.articles.length > 1 && (
                    <div className="mt-8">
                      <h3 className="text-lg font-semibold text-foreground mb-4">Artigos Relacionados</h3>
                      <div className="grid gap-3">
                        {selectedCategory.articles
                          .filter(a => a.id !== selectedArticle.id)
                          .slice(0, 3)
                          .map((article) => (
                            <button
                              key={article.id}
                              onClick={() => handleArticleClick(article, selectedCategory)}
                              className="text-left glass-card rounded-lg p-4 hover:border-primary/50 transition-all group flex items-center justify-between"
                            >
                              <span className="text-sm text-foreground group-hover:text-primary transition-colors">
                                {article.title}
                              </span>
                              <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              ) : selectedCategory ? (
                /* Category View */
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                >
                  <button
                    onClick={handleBackToHome}
                    className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors mb-6"
                  >
                    <ArrowLeft size={16} />
                    Voltar para Central de Ajuda
                  </button>

                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center text-primary">
                      {selectedCategory.icon}
                    </div>
                    <div>
                      <h1 className="text-2xl md:text-3xl font-bold text-foreground">
                        {selectedCategory.title}
                      </h1>
                      <p className="text-muted-foreground text-sm">
                        {selectedCategory.description}
                      </p>
                    </div>
                  </div>

                  <div className="space-y-3">
                    {selectedCategory.articles.map((article, index) => (
                      <motion.button
                        key={article.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: index * 0.05 }}
                        onClick={() => handleArticleClick(article, selectedCategory)}
                        className="w-full text-left glass-card rounded-xl p-5 hover:border-primary/50 transition-all group flex items-center justify-between"
                      >
                        <div>
                          <h3 className="font-medium text-foreground group-hover:text-primary transition-colors">
                            {article.title}
                          </h3>
                          <p className="text-sm text-muted-foreground line-clamp-1 mt-1">
                            {article.content.substring(0, 100)}...
                          </p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors flex-shrink-0" />
                      </motion.button>
                    ))}
                  </div>
                </motion.div>
              ) : null}

            </div>
          </main>

          {/* Right Sidebar - Table of Contents (Desktop only) */}
          {selectedArticle && (
            <aside className="hidden xl:block fixed right-0 top-20 bottom-0 w-56 border-l border-border bg-card/30 p-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Nesta página
              </h4>
              <nav className="space-y-2">
                <a href="#" className="block text-sm text-primary hover:text-primary/80 transition-colors">
                  {selectedArticle.title}
                </a>
              </nav>
              
              <div className="mt-8 pt-4 border-t border-border">
                <p className="text-xs text-muted-foreground mb-2">Este artigo foi útil?</p>
                <div className="flex gap-2">
                  <button className="px-3 py-1 text-xs bg-muted hover:bg-muted/80 rounded transition-colors">
                    👍 Sim
                  </button>
                  <button className="px-3 py-1 text-xs bg-muted hover:bg-muted/80 rounded transition-colors">
                    👎 Não
                  </button>
                </div>
              </div>
            </aside>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="lg:ml-72 py-6 border-t border-border bg-card/30">
        <div className="container mx-auto px-4 text-center">
          <p className="text-xs text-muted-foreground">
            © 2026 Zavvi.ai - Central de Ajuda | Todos os direitos reservados
          </p>
        </div>
      </footer>
    </div>
  );
};

export default HelpCenter;
